import React, { useEffect, useState, useCallback } from "react";
import { FileText, Plus, Phone, Mail, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api.js";
import { toast } from "sonner";
import { VehicleTriplePicker } from "../lib/vehicleCatalogue.jsx";

const STATUS_COLORS = {
  "Novo pedido": "bg-[#E7F0FF] text-[#1F3864] border-[#1F3864]/30",
  "Em curso": "bg-[#FFF5CC] text-[#8A6D00] border-[#8A6D00]/30",
  "Cotação enviada": "bg-[#D1EBFF] text-[#1A5490] border-[#1A5490]/30",
  "Follow-up": "bg-[#FFE2CC] text-[#A24A00] border-[#A24A00]/30",
  "Cliente a decidir": "bg-[#FFE8EE] text-[#A62139] border-[#A62139]/30",
  "Encomenda efectuada": "bg-[#D1F2E1] text-[#0F5132] border-[#28A745]/30",
  "Perdido": "bg-[#F8D7DA] text-[#721C24] border-[#DC3545]/30",
  "Cancelado": "bg-[#E8E8E8] text-[#505050] border-[#A0A0A0]/30",
};

const URGENCY_COLORS = {
  "Baixa": "text-[#505050]",
  "Normal": "text-[#1F3864]",
  "Alta": "text-[#A24A00]",
  "Muito urgente": "text-[#DC3545] font-bold",
};

export default function VehicleQuotesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enums, setEnums] = useState({ statuses: [], urgencies: [], lost_reasons: [] });
  const [filterStatus, setFilterStatus] = useState("");
  const [openOnly, setOpenOnly] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { open_only: openOnly };
      if (filterStatus) params.status = filterStatus;
      const { data } = await api.get("/vehicle-quotes", { params });
      setItems(data);
    } catch (e) {
      toast.error("Falha ao carregar VRQs");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, openOnly]);

  useEffect(() => {
    api.get("/vehicle-quotes/enums").then((r) => setEnums(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#1F3864]" strokeWidth={1.8} />
            Cotações de Viaturas (VRQ)
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">
            Pipeline comercial — pedidos de cotação por vendedor
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="vrq-new-btn"
          className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-4 py-2 font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nova VRQ
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex items-center gap-3 flex-wrap">
        <label className="text-xs text-[#6C757D]">Estado</label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="vrq-filter-status"
          className="text-sm border border-gray-200 rounded-sm px-2 py-1"
        >
          <option value="">Todos</option>
          {enums.statuses.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <label className="text-xs text-[#6C757D] flex items-center gap-1 ml-3">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => setOpenOnly(e.target.checked)}
            data-testid="vrq-filter-open-only"
          />
          Apenas abertas
        </label>
        <div className="ml-auto text-xs text-[#6C757D]">{items.length} resultados</div>
      </div>

      {/* List */}
      {loading ? (
        <div className="p-12 text-center text-[#6C757D]"><Loader2 className="w-6 h-6 inline animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="p-12 text-center text-[#6C757D] bg-white border border-gray-200 rounded-md" data-testid="vrq-empty-state">
          Nenhuma VRQ encontrada. Cria uma nova para começar.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6F8] border-b border-gray-200 text-[11px] uppercase tracking-wider text-[#6C757D]">
              <tr>
                <th className="text-left px-3 py-2">VRQ #</th>
                <th className="text-left px-3 py-2">Cliente</th>
                <th className="text-left px-3 py-2">Viatura</th>
                <th className="text-left px-3 py-2">Vendedor</th>
                <th className="text-left px-3 py-2">Urgência</th>
                <th className="text-left px-3 py-2">Follow-ups</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <tr
                  key={v.id}
                  data-testid={`vrq-row-${v.id}`}
                  onClick={() => setDetailId(v.id)}
                  className="border-b border-gray-100 last:border-0 hover:bg-[#F4F6F8] cursor-pointer"
                >
                  <td className="px-3 py-2 font-mono text-[#1F3864]">{v.vrq_number}</td>
                  <td className="px-3 py-2">{v.customer_name}</td>
                  <td className="px-3 py-2">{v.brand} {v.model}{v.year ? ` · ${v.year}` : ""}</td>
                  <td className="px-3 py-2 text-xs text-[#505050]">{v.salesperson_name || "—"}</td>
                  <td className={`px-3 py-2 text-xs ${URGENCY_COLORS[v.urgency] || ""}`}>
                    {v.urgency === "Muito urgente" && <AlertCircle className="w-3 h-3 inline mr-1" />}
                    {v.urgency}
                  </td>
                  <td className="px-3 py-2 text-xs">{v.follow_up_count || 0}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-sm border ${STATUS_COLORS[v.status] || ""}`}>
                      {v.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-[#1F3864]">Abrir →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <VrqCreateDialog
          enums={enums}
          onClose={() => setShowCreate(false)}
          onCreated={(created) => {
            setShowCreate(false);
            toast.success(`VRQ ${created.vrq_number} criada`);
            setDetailId(created.id);
            load();
          }}
        />
      )}

      {detailId && (
        <VrqDetailDrawer
          vrqId={detailId}
          enums={enums}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ====================================================================
// Create dialog
// ====================================================================
function VrqCreateDialog({ enums, onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    brand: "",
    model: "",
    vehicle_generation: "",
    year: "",
    colour: "",
    estimated_price: "",
    urgency: "Normal",
    financing_required: false,
    trade_in_vehicle: "",
    notes: "",
  });
  const [brands, setBrands] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data.filter((b) => b.active))).catch(() => {});
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.customer_name || !form.brand || !form.model) {
      setErr("Preenche cliente, marca e modelo");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year, 10) : null,
        estimated_price: form.estimated_price ? parseFloat(form.estimated_price) : null,
      };
      const { data } = await api.post("/vehicle-quotes", payload);
      onCreated(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Falha ao criar VRQ");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" data-testid="vrq-create-dialog">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">Nova VRQ</h2>
          <button type="button" onClick={onClose} className="text-sm text-[#6C757D] hover:text-[#1A1A2E]">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Nome do cliente *" value={form.customer_name} onChange={(v) => set("customer_name", v)} testid="vrq-customer-name" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefone" value={form.customer_phone} onChange={(v) => set("customer_phone", v)} testid="vrq-customer-phone" />
            <Field label="Email" value={form.customer_email} onChange={(v) => set("customer_email", v)} testid="vrq-customer-email" />
          </div>
          <div>
            <label className="text-xs text-[#6C757D] block mb-1">Viatura (Marca · Modelo · Geração) *</label>
            <VehicleTriplePicker
              brand={form.brand}
              model={form.model}
              generation={form.vehicle_generation}
              onChange={({ brand, model, generation }) =>
                setForm((f) => ({ ...f, brand: brand || "", model: model || "", vehicle_generation: generation || "" }))
              }
              required
              testidPrefix="vrq"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Ano" type="number" value={form.year} onChange={(v) => set("year", v)} testid="vrq-year" />
            <Field label="Cor" value={form.colour} onChange={(v) => set("colour", v)} testid="vrq-colour" />
            <Field label="Preço est. (MZN)" type="number" value={form.estimated_price} onChange={(v) => set("estimated_price", v)} testid="vrq-price" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Urgência" value={form.urgency} onChange={(v) => set("urgency", v)} options={enums.urgencies} testid="vrq-urgency" />
            <div className="flex items-end">
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.financing_required}
                  onChange={(e) => set("financing_required", e.target.checked)}
                  data-testid="vrq-financing"
                />
                Precisa de financiamento
              </label>
            </div>
          </div>
          <Field label="Troca (retoma)" value={form.trade_in_vehicle} onChange={(v) => set("trade_in_vehicle", v)} testid="vrq-tradein" />
          <Textarea label="Notas" value={form.notes} onChange={(v) => set("notes", v)} testid="vrq-notes" />
          {err && <div className="text-xs text-[#DC3545] bg-[#F8D7DA] px-3 py-2 rounded-sm">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 border border-gray-200 rounded-sm">Cancelar</button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="vrq-submit-btn"
            className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-4 py-1.5 font-medium disabled:opacity-60"
          >
            {submitting ? "A criar…" : "Criar VRQ"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ====================================================================
// Detail drawer
// ====================================================================
function VrqDetailDrawer({ vrqId, enums, onClose, onChanged }) {
  const [vrq, setVrq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // "status" | "followup" | "convert"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vehicle-quotes/${vrqId}`);
      setVrq(data);
    } catch (e) {
      toast.error("VRQ não encontrada");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [vrqId, onClose]);

  useEffect(() => { load(); }, [load]);

  const isClosed = ["Encomenda efectuada", "Perdido", "Cancelado"].includes(vrq?.status);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end">
      <div className="bg-white w-full max-w-2xl flex flex-col shadow-xl" data-testid="vrq-detail-drawer">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">
            {vrq?.vrq_number || "VRQ"}
          </h2>
          <button onClick={onClose} className="text-sm text-[#6C757D] hover:text-[#1A1A2E]" data-testid="vrq-drawer-close">✕</button>
        </div>

        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 inline animate-spin" /></div>
        ) : vrq && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-sm border ${STATUS_COLORS[vrq.status] || ""}`} data-testid="vrq-status-badge">{vrq.status}</span>
              <span className={`text-xs ${URGENCY_COLORS[vrq.urgency] || ""}`}>{vrq.urgency}</span>
              {vrq.converted_vr_id && (
                <span className="text-xs bg-[#D1F2E1] text-[#0F5132] border border-[#28A745]/30 px-2 py-0.5 rounded-sm">
                  Convertida em VR
                </span>
              )}
            </div>

            <section className="border border-gray-200 rounded-md p-3">
              <div className="text-[11px] uppercase tracking-wider text-[#6C757D] mb-2">Cliente</div>
              <div className="text-sm text-[#1A1A2E] font-semibold">{vrq.customer_name}</div>
              {vrq.customer_phone && <div className="text-xs flex items-center gap-1 text-[#505050]"><Phone className="w-3 h-3" /> {vrq.customer_phone}</div>}
              {vrq.customer_email && <div className="text-xs flex items-center gap-1 text-[#505050]"><Mail className="w-3 h-3" /> {vrq.customer_email}</div>}
            </section>

            <section className="border border-gray-200 rounded-md p-3">
              <div className="text-[11px] uppercase tracking-wider text-[#6C757D] mb-2">Viatura</div>
              <div className="text-sm">{vrq.brand} {vrq.model} {vrq.year ? `(${vrq.year})` : ""}</div>
              {vrq.colour && <div className="text-xs text-[#505050]">Cor: {vrq.colour}</div>}
              {vrq.estimated_price && <div className="text-xs text-[#505050]">Preço estimado: {vrq.estimated_price.toLocaleString()} MZN</div>}
              {vrq.financing_required && <div className="text-xs text-[#1F3864] mt-1">⚑ Precisa de financiamento</div>}
              {vrq.trade_in_vehicle && <div className="text-xs text-[#505050]">Troca: {vrq.trade_in_vehicle}</div>}
            </section>

            {vrq.notes && (
              <section className="border border-gray-200 rounded-md p-3">
                <div className="text-[11px] uppercase tracking-wider text-[#6C757D] mb-2">Notas</div>
                <div className="text-sm whitespace-pre-wrap">{vrq.notes}</div>
              </section>
            )}

            {vrq.motivo_perdido && (
              <section className="border border-[#DC3545]/30 rounded-md p-3 bg-[#F8D7DA]/30">
                <div className="text-[11px] uppercase tracking-wider text-[#721C24] mb-1">Motivo perdido</div>
                <div className="text-sm text-[#721C24]">{vrq.motivo_perdido}</div>
              </section>
            )}

            {vrq.alidata_sale_reference && (
              <section className="border border-gray-200 rounded-md p-3">
                <div className="text-[11px] uppercase tracking-wider text-[#6C757D] mb-1">Alidata — venda</div>
                <div className="text-sm font-mono">{vrq.alidata_sale_reference}</div>
              </section>
            )}

            <section className="border border-gray-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-[#6C757D]">Follow-ups ({vrq.follow_ups?.length || 0})</div>
                {!isClosed && (
                  <button
                    onClick={() => setAction("followup")}
                    data-testid="vrq-add-followup-btn"
                    className="text-xs text-[#1F3864] hover:underline"
                  >
                    + Novo follow-up
                  </button>
                )}
              </div>
              {(vrq.follow_ups || []).length === 0 ? (
                <div className="text-xs text-[#6C757D]">Sem registos.</div>
              ) : (
                <div className="space-y-2">
                  {vrq.follow_ups.map((f) => (
                    <div key={f.id} className="text-xs border-l-2 border-[#1F3864] pl-2">
                      <div className="font-semibold text-[#1A1A2E]">{f.method} · {f.result}</div>
                      {f.notes && <div className="text-[#505050]">{f.notes}</div>}
                      <div className="text-[10px] text-[#6C757D]">{f.logged_by_name} · {new Date(f.logged_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        <div className="px-5 py-3 border-t border-gray-200 flex items-center gap-2 flex-wrap">
          {!isClosed && (
            <button
              onClick={() => setAction("status")}
              data-testid="vrq-change-status-btn"
              className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-3 py-1.5 font-medium"
            >
              Alterar estado
            </button>
          )}
          {vrq?.status === "Encomenda efectuada" && !vrq?.converted_vr_id && (
            <button
              onClick={() => setAction("convert")}
              data-testid="vrq-convert-btn"
              className="text-sm bg-[#28A745] hover:bg-[#1E7E34] text-white rounded-sm px-3 py-1.5 font-medium"
            >
              Converter em VR
            </button>
          )}
          <button onClick={onClose} className="ml-auto text-sm px-3 py-1.5 border border-gray-200 rounded-sm">Fechar</button>
        </div>

        {action === "status" && (
          <StatusChangeDialog
            enums={enums}
            vrq={vrq}
            onClose={() => setAction(null)}
            onSaved={async () => { setAction(null); await load(); onChanged?.(); }}
          />
        )}
        {action === "followup" && (
          <FollowUpDialog
            vrq={vrq}
            onClose={() => setAction(null)}
            onSaved={async () => { setAction(null); await load(); onChanged?.(); }}
          />
        )}
        {action === "convert" && (
          <ConvertDialog
            vrq={vrq}
            onClose={() => setAction(null)}
            onSaved={async () => { setAction(null); await load(); onChanged?.(); }}
          />
        )}
      </div>
    </div>
  );
}

function StatusChangeDialog({ enums, vrq, onClose, onSaved }) {
  const [newStatus, setNewStatus] = useState("");
  const [comment, setComment] = useState("");
  const [motivo, setMotivo] = useState("");
  const [alidataRef, setAlidataRef] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!newStatus || !comment) {
      setErr("Estado e comentário são obrigatórios");
      return;
    }
    setSaving(true);
    try {
      const payload = { new_status: newStatus, comment };
      if (newStatus === "Perdido") payload.motivo_perdido = motivo;
      if (newStatus === "Encomenda efectuada") payload.alidata_sale_reference = alidataRef || null;
      await api.post(`/vehicle-quotes/${vrq.id}/status`, payload);
      toast.success(`Estado alterado para ${newStatus}`);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Falha ao alterar estado");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md shadow-xl max-w-md w-full" data-testid="vrq-status-dialog">
        <div className="px-5 py-3 border-b border-gray-200">
          <h3 className="font-heading text-lg font-bold">Alterar estado</h3>
        </div>
        <div className="p-5 space-y-3">
          <Select
            label="Novo estado"
            value={newStatus}
            onChange={setNewStatus}
            options={enums.statuses.filter((s) => s !== vrq.status)}
            testid="vrq-status-select"
          />
          {newStatus === "Perdido" && (
            <Select
              label="Motivo perdido *"
              value={motivo}
              onChange={setMotivo}
              options={enums.lost_reasons || []}
              testid="vrq-lost-reason"
            />
          )}
          {newStatus === "Encomenda efectuada" && (
            <Field label="Alidata ref. venda" value={alidataRef} onChange={setAlidataRef} testid="vrq-alidata-ref" />
          )}
          <Textarea label="Comentário *" value={comment} onChange={setComment} testid="vrq-status-comment" />
          {err && <div className="text-xs text-[#DC3545]">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 border border-gray-200 rounded-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="vrq-status-save" className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-4 py-1.5 font-medium disabled:opacity-60">
            {saving ? "A guardar…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FollowUpDialog({ vrq, onClose, onSaved }) {
  const [method, setMethod] = useState("Phone");
  const [result, setResult] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!result) { setErr("Resultado é obrigatório"); return; }
    setSaving(true);
    try {
      await api.post(`/vehicle-quotes/${vrq.id}/follow-ups`, { method, result, notes });
      toast.success("Follow-up registado");
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Falha ao registar follow-up");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md shadow-xl max-w-md w-full" data-testid="vrq-followup-dialog">
        <div className="px-5 py-3 border-b border-gray-200">
          <h3 className="font-heading text-lg font-bold">Novo follow-up</h3>
        </div>
        <div className="p-5 space-y-3">
          <Select label="Método" value={method} onChange={setMethod} options={["Phone", "WhatsApp", "Email", "In person"]} testid="vrq-fu-method" />
          <Field label="Resultado *" value={result} onChange={setResult} testid="vrq-fu-result" />
          <Textarea label="Notas" value={notes} onChange={setNotes} testid="vrq-fu-notes" />
          {err && <div className="text-xs text-[#DC3545]">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 border border-gray-200 rounded-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="vrq-fu-save" className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-4 py-1.5 font-medium disabled:opacity-60">
            {saving ? "A guardar…" : "Registar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConvertDialog({ vrq, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/vehicle-quotes/${vrq.id}/convert-to-vr`);
      toast.success(`Convertida em VR ${data.vr_number}`);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Falha ao converter");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md shadow-xl max-w-md w-full" data-testid="vrq-convert-dialog">
        <div className="px-5 py-3 border-b border-gray-200">
          <h3 className="font-heading text-lg font-bold">Converter em VR</h3>
        </div>
        <div className="p-5 space-y-3 text-sm text-[#505050]">
          <p>Esta ação criará um novo Registo de Viatura (VR) com os dados da VRQ. A matrícula e o VIN ficam pendentes até à entrega.</p>
          {err && <div className="text-xs text-[#DC3545]">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 border border-gray-200 rounded-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="vrq-convert-confirm" className="text-sm bg-[#28A745] hover:bg-[#1E7E34] text-white rounded-sm px-4 py-1.5 font-medium disabled:opacity-60">
            {saving ? "A converter…" : "Confirmar conversão"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ====================================================================
// Small form controls
// ====================================================================
function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs text-[#6C757D] block mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full text-sm border border-gray-200 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs text-[#6C757D] block mb-1">{label}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full text-sm border border-gray-200 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30 bg-white"
      >
        <option value="">— seleccionar —</option>
        {options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </div>
  );
}

function Textarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-[#6C757D] block mb-1">{label}</label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        rows={3}
        className="w-full text-sm border border-gray-200 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
      />
    </div>
  );
}
